import { useEffect, useRef, useState } from 'react';

/**
 * Polls the given async loader on a schedule that adapts to user activity:
 * - 5s while the tab is focused AND the user is interacting
 * - 15s while the tab is focused but idle for >60s
 * - 60s while the tab is hidden
 * Pauses on offline, fires an immediate poll when back online or tab becomes visible.
 *
 * Loader must be stable (wrap with useCallback) or pass stable deps.
 */
export function useSmartPoll(loader, {
  activeMs = 5000,
  idleMs = 15000,
  hiddenMs = 60000,
  idleAfterMs = 60000,
  enabled = true,
} = {}) {
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const timer = useRef(null);
  const lastActivity = useRef(Date.now());
  const isPolling = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const bump = () => { lastActivity.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(ev => window.addEventListener(ev, bump, { passive: true }));

    const schedule = () => {
      clearTimeout(timer.current);
      const hidden = document.hidden;
      const idle = Date.now() - lastActivity.current > idleAfterMs;
      const wait = hidden ? hiddenMs : idle ? idleMs : activeMs;
      timer.current = setTimeout(tick, wait);
    };

    const tick = async () => {
      if (!navigator.onLine || isPolling.current) { schedule(); return; }
      isPolling.current = true;
      try {
        await loader();
        setLastLoadedAt(Date.now());
      } catch (_) {
        // silent — caller handles errors
      } finally {
        isPolling.current = false;
        schedule();
      }
    };

    const onVisibility = () => { if (!document.hidden) tick(); else schedule(); };
    const onOnline = () => tick();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);

    schedule();

    return () => {
      clearTimeout(timer.current);
      events.forEach(ev => window.removeEventListener(ev, bump));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, [loader, activeMs, idleMs, hiddenMs, idleAfterMs, enabled]);

  return { lastLoadedAt };
}
